import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useCreateAlert } from "../../api/createAlert";
import { useDeleteAlert } from "../../api/deleteAlert";
import { useAlertData } from "../../api/getAlert";
import { type UpdateAlertParams, useUpdateAlert } from "../../api/updateAlert";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

function isValidUpdateAlertParams(
	params: unknown,
): params is UpdateAlertParams {
	return typeof params === "object" && params !== null;
}

const ActiveAlerts = () => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [kwhGoal, setKwhGoal] = useState("");
	const [euroBudget, setEuroBudget] = useState("");
	const [alertsEnabled, setAlertsEnabled] = useState(true);
	const [unit, setUnit] = useState<"kWh" | "EUR">("kWh");
	const [isEditing, setIsEditing] = useState(false);

	const { mutate: updateAlert } = useUpdateAlert();
	const { mutateAsync: deleteAlert, isPending: isDeleting } = useDeleteAlert();
	const { mutate: createAlert } = useCreateAlert();

	const { data: alertData, isLoading: isAlertLoading } = useAlertData({
		energy: "ELEC",
	});

	const hasAlerts =
		alertData?.alerts_details?.some(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? false;
	const currentAlert =
		alertData?.alerts_details?.find(
			(alert) =>
				alert.alert_type === "USAGE_LIMIT_CONSUMPTION_MONTHLY" ||
				alert.alert_type === "USAGE_LIMIT_COST_MONTHLY",
		) ?? null;

	useEffect(() => {
		if (currentAlert) {
			setUnit(currentAlert.unit as "kWh" | "EUR");
			if (currentAlert.unit === "kWh") {
				setKwhGoal(currentAlert.value.toString());
				setEuroBudget("");
			} else {
				setEuroBudget(currentAlert.value?.toString());
				setKwhGoal("");
			}
			setAlertsEnabled(true);
		}
	}, [currentAlert]);

	const handleOpenDialog = (editing: boolean) => {
		setIsEditing(editing);
		if (editing && currentAlert) {
			setUnit(currentAlert.unit as "kWh" | "EUR");
			if (currentAlert.unit === "kWh") {
				setKwhGoal(currentAlert.value.toString());
				setEuroBudget("");
			} else {
				setEuroBudget(currentAlert.value.toString());
				setKwhGoal("");
			}
			setAlertsEnabled(true);
		} else {
			setUnit("kWh");
			setKwhGoal("");
			setEuroBudget("");
			setAlertsEnabled(false);
		}
		setDialogOpen(true);
	};

	const handleSubmit = () => {
		const value = unit === "kWh" ? Number(kwhGoal) : Number(euroBudget);
		const payload = {
			energy: "ELEC",
			alert_type:
				unit === "kWh"
					? ("USAGE_LIMIT_CONSUMPTION_MONTHLY" as const)
					: ("USAGE_LIMIT_COST_MONTHLY" as const),
			value,
			unit: unit === "kWh" ? ("kWh" as const) : ("EUR" as const),
			is_active: alertsEnabled,
		};

		if (isEditing && currentAlert) {
			const data = {
				...payload,
				alert_id: currentAlert.alert_id,
				energy: "ELEC",
			};

			if (isValidUpdateAlertParams(data)) {
				updateAlert(data);
			}
		} else {
			const data = { ...payload, energy: "ELEC" };
			if (isValidUpdateAlertParams(data)) {
				createAlert(data);
			}
		}

		setDialogOpen(false);
	};

	const handleDelete = async () => {
		if (!currentAlert) return;
		try {
			await deleteAlert({ alert_id: currentAlert.alert_id });
			setKwhGoal("");
			setEuroBudget("");
			setAlertsEnabled(false);
		} catch (error) {
			console.error("Failed to delete alert:", error);
		}
	};

	if (isAlertLoading) {
		return (
			<Card className="rounded-2xl border p-2 gap-0">
				<CardHeader className="p-2">
					<CardTitle className="text-lg font-semibold text-gray-800">
						Mon budget
					</CardTitle>
				</CardHeader>
				<CardContent className="p-2">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<>
			<Card className="rounded-2xl border p-2 gap-0">
				<CardHeader className="p-2">
					<CardTitle className="text-lg font-semibold text-gray-800">
						Mon budget
					</CardTitle>
				</CardHeader>
				<CardContent className="p-2">
					<div className="flex justify-between items-center bg-gray-100 rounded-xl px-4 py-4">
						<div>
							<p className="font-semibold text-gray-800">
								Électricité :{" "}
								{currentAlert
									? `${currentAlert.value} ${currentAlert.unit}`
									: "Aucun budget défini"}
							</p>
						</div>
						<div className="flex gap-2">
							{hasAlerts ? (
								<>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 hover:bg-pink-100"
										onClick={() => handleOpenDialog(true)}
									>
										<Pencil className="h-4 w-4 text-black" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 hover:bg-red-100"
										onClick={handleDelete}
										disabled={isDeleting}
									>
										<Trash2 className="h-4 w-4 text-red-500" />
									</Button>
								</>
							) : (
								<Button
									className="bg-pink-500 text-white"
									onClick={() => handleOpenDialog(false)}
								>
									Définir un budget
								</Button>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="sm:max-w-sm w-[95%] md:h-[40%] md:max-w-full rounded-2xl gap-0">
					<DialogHeader className="text-start px-2">
						<DialogTitle className="text-lg font-semibold">
							{isEditing ? "Modifier le budget" : "Définir un budget"}
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-4 p-2">
						<Tabs
							value={unit}
							onValueChange={(val) => setUnit(val as "kWh" | "EUR")}
							className="w-full mb-4"
						>
							<TabsList className="w-full grid grid-cols-2">
								<TabsTrigger value="kWh">kWh</TabsTrigger>
								<TabsTrigger value="EUR">Euro</TabsTrigger>
							</TabsList>
						</Tabs>

						<div className="space-y-2">
							<label
								htmlFor="goal"
								className="block text-sm font-medium text-gray-700"
							>
								{unit === "kWh" ? "Consommation (kWh)" : "Budget (€)"}
							</label>
							<div className="relative">
								<Input
									id="goal"
									type="number"
									inputMode="decimal"
									value={unit === "kWh" ? kwhGoal : euroBudget}
									onChange={(e) => {
										const schema = z
											.number({ coerce: true })
											.max(999999)
											.safeParse(e.target.value);

										if (!schema.data) {
											setKwhGoal("");
											setEuroBudget("");
											return;
										}

										if (unit === "kWh") {
											setKwhGoal(schema.data?.toString() ?? "");
										} else {
											setEuroBudget(schema.data?.toString() ?? "");
										}
									}}
									placeholder={unit === "kWh" ? "0.0" : "0.00"}
									className="w-full pr-12"
								/>
								<span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
									{unit === "kWh" ? "kWh" : "€"}
								</span>
							</div>
						</div>

						<div className="flex items-center space-x-2">
							<Switch
								checked={alertsEnabled}
								onCheckedChange={setAlertsEnabled}
							/>
							<p className="text-sm">Activer les alertes de dépassement</p>
						</div>

						<p className="text-sm text-gray-500">
							Vous serez alerté lorsque votre budget dépassera 90%, 100% et 150%
							de la limite définie
						</p>

						<Button
							className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2"
							onClick={handleSubmit}
						>
							Enregistrer
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ActiveAlerts;
